/**
 * Query Builder Dashboard
 * Powers /pages/query-builder. Fetches products from the chosen collection,
 * runs them through the same parser/evaluator as the live search modal
 * (window.CollectionSearchQuery), and renders a live-updating preview.
 */
(function (window, document) {
  'use strict';

  const PRODUCTS_PER_PAGE = 250;
  const DEBOUNCE_MS = 250;
  const PREVIEW_PAGE = 60;
  const FETCH_RETRIES = 3;
  const RETRY_BACKOFF_MS = 800;
  const PAGE_DELAY_MS = 150;

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Fetch with retry-on-transient-error. Shopify's /products.json endpoint
  // occasionally returns 403 or 429 under bot-protection / rate-limiting, even
  // for legitimate same-origin requests. A short backoff usually resolves it.
  async function fetchWithRetry(url) {
    let lastErr;
    for (let attempt = 0; attempt < FETCH_RETRIES; attempt++) {
      try {
        const response = await fetch(url, { credentials: 'same-origin' });
        if (response.ok) return response;
        if (response.status === 403 || response.status === 429 || response.status >= 500) {
          lastErr = new Error('HTTP ' + response.status);
          await sleep(RETRY_BACKOFF_MS * (attempt + 1));
          continue;
        }
        // Other status codes (404, etc.) aren't going to recover from retries.
        throw new Error('HTTP ' + response.status);
      } catch (err) {
        lastErr = err;
        if (attempt === FETCH_RETRIES - 1) break;
        await sleep(RETRY_BACKOFF_MS * (attempt + 1));
      }
    }
    throw lastErr || new Error('Request failed');
  }

  function waitForQueryLib() {
    return new Promise((resolve) => {
      if (window.CollectionSearchQuery) return resolve(window.CollectionSearchQuery);
      const t = setInterval(() => {
        if (window.CollectionSearchQuery) {
          clearInterval(t);
          resolve(window.CollectionSearchQuery);
        }
      }, 50);
    });
  }

  function escapeHtml(text) {
    if (text == null) return '';
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeRegex(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function highlightTitle(title, parsed) {
    let html = escapeHtml(title);
    if (!parsed) return html;
    const needles = [];
    parsed.clauses.forEach((c) => {
      if (c.negate) return;
      if (c.field && c.field !== 'title' && c.field !== 'handle' && c.field !== 'body' && c.field !== 'tag' && c.field !== 'vendor' && c.field !== 'type') return;
      if (c.value) needles.push(c.value);
    });
    needles.forEach((needle) => {
      const re = new RegExp('(' + escapeRegex(needle) + ')', 'gi');
      html = html.replace(re, '<mark>$1</mark>');
    });
    return html;
  }

  class QueryBuilder {
    constructor(root) {
      this.root = root;
      this.collectionSelect = root.querySelector('[data-qb-collection]');
      this.queryInput = root.querySelector('[data-qb-query]');
      this.statusEl = root.querySelector('[data-qb-status]');
      this.parsedEl = root.querySelector('[data-qb-parsed]');
      this.resultsEl = root.querySelector('[data-qb-results]');
      this.copyBtn = root.querySelector('[data-qb-copy]');
      this.clearBtn = root.querySelector('[data-qb-clear]');
      this.builderEl = root.querySelector('[data-qb-builder]');
      this.builderOp = root.querySelector('[data-qb-builder-op]');
      this.builderField = root.querySelector('[data-qb-builder-field]');
      this.builderValue = root.querySelector('[data-qb-builder-value]');
      this.builderAdd = root.querySelector('[data-qb-builder-add]');
      this.builderHint = root.querySelector('[data-qb-builder-hint]');
      this.typeaheadEl = root.querySelector('[data-qb-typeahead]');
      this.typeaheadActive = -1;

      this.products = [];
      this.currentHandle = '';
      this.isFetching = false;
      this.fetchToken = 0;
      this.debounceTimer = null;
      this.previewLimit = PREVIEW_PAGE;

      this.bind();
    }

    bind() {
      this.collectionSelect.addEventListener('change', () => {
        this.previewLimit = PREVIEW_PAGE;
        this.loadCollection(this.collectionSelect.value);
      });

      this.queryInput.addEventListener('input', () => {
        this.previewLimit = PREVIEW_PAGE;
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.render(), DEBOUNCE_MS);
      });

      this.copyBtn.addEventListener('click', () => this.copyQuery());

      this.clearBtn.addEventListener('click', () => {
        this.queryInput.value = '';
        this.previewLimit = PREVIEW_PAGE;
        this.render();
        this.queryInput.focus();
      });

      this.resultsEl.addEventListener('click', (e) => {
        const pinBtn = e.target.closest('.qb-card-pin-btn');
        if (pinBtn) {
          this.togglePin(pinBtn.dataset.handle);
          return;
        }
        const loadMore = e.target.closest('[data-qb-load-more]');
        if (loadMore) {
          this.previewLimit += PREVIEW_PAGE;
          this.render();
        }
      });

      this.builderAdd.addEventListener('click', () => this.addRule());
      this.builderValue.addEventListener('keydown', (e) => this.onValueKeydown(e));
      this.builderValue.addEventListener('input', () => this.refreshTypeahead());
      this.builderValue.addEventListener('focus', () => this.refreshTypeahead());
      this.builderValue.addEventListener('blur', () => {
        // Delay so clicks on items register first
        setTimeout(() => this.hideTypeahead(), 150);
      });
      this.typeaheadEl.addEventListener('mousedown', (e) => {
        const item = e.target.closest('.qb-typeahead-item');
        if (!item) return;
        e.preventDefault();
        this.selectTypeahead(item.dataset.handle);
      });
      this.builderOp.addEventListener('change', () => this.updateBuilderMode());
      this.updateBuilderMode();
    }

    onValueKeydown(e) {
      const visible = !this.typeaheadEl.hidden;
      if (visible && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault();
        const items = this.typeaheadEl.querySelectorAll('.qb-typeahead-item');
        if (!items.length) return;
        this.typeaheadActive += e.key === 'ArrowDown' ? 1 : -1;
        if (this.typeaheadActive < 0) this.typeaheadActive = items.length - 1;
        if (this.typeaheadActive >= items.length) this.typeaheadActive = 0;
        items.forEach((el, i) => el.classList.toggle('is-active', i === this.typeaheadActive));
        items[this.typeaheadActive].scrollIntoView({ block: 'nearest' });
        return;
      }
      if (visible && e.key === 'Enter') {
        const active = this.typeaheadEl.querySelector('.qb-typeahead-item.is-active');
        if (active) {
          e.preventDefault();
          this.selectTypeahead(active.dataset.handle);
          return;
        }
      }
      if (e.key === 'Escape' && visible) {
        e.preventDefault();
        this.hideTypeahead();
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        this.addRule();
      }
    }

    refreshTypeahead() {
      if (this.builderOp.value !== 'pin') {
        this.hideTypeahead();
        return;
      }
      const q = (this.builderValue.value || '').toLowerCase().trim();
      const matches = this.products
        .filter((p) => {
          if (!q) return true;
          const handle = (p.handle || '').toLowerCase();
          const title = (p.title || '').toLowerCase();
          return handle.indexOf(q) !== -1 || title.indexOf(q) !== -1;
        })
        .slice(0, 25);
      this.renderTypeahead(matches);
    }

    renderTypeahead(matches) {
      if (!this.products.length) {
        this.typeaheadEl.innerHTML = '<div class="qb-typeahead-empty">Select a collection to load products.</div>';
        this.typeaheadEl.hidden = false;
        this.typeaheadActive = -1;
        return;
      }
      if (!matches.length) {
        this.typeaheadEl.innerHTML = '<div class="qb-typeahead-empty">No products match that search.</div>';
        this.typeaheadEl.hidden = false;
        this.typeaheadActive = -1;
        return;
      }
      this.typeaheadEl.innerHTML = matches.map((p) => {
        const handle = escapeHtml(p.handle || '');
        const title = escapeHtml(p.title || '');
        return '<div class="qb-typeahead-item" data-handle="' + handle + '">' +
          '<div class="qb-typeahead-title">' + title + '</div>' +
          '<div class="qb-typeahead-handle">' + handle + '</div>' +
          '</div>';
      }).join('');
      this.typeaheadEl.hidden = false;
      this.typeaheadActive = -1;
    }

    hideTypeahead() {
      this.typeaheadEl.hidden = true;
      this.typeaheadActive = -1;
    }

    selectTypeahead(handle) {
      this.builderValue.value = handle;
      this.hideTypeahead();
      this.addRule();
    }

    updateBuilderMode() {
      const op = this.builderOp.value;
      const isPin = op === 'pin';
      this.builderEl.classList.toggle('is-pin', isPin);

      if (isPin) {
        this.builderValue.placeholder = 'Type a product title to search…';
      } else if (op === 'boost') {
        this.builderValue.placeholder = 'word(s) to pin to the top';
      } else if (op === 'phrase' || op === 'exclude-phrase') {
        this.builderValue.placeholder = 'exact word or phrase';
      } else if (op === 'exclude') {
        this.builderValue.placeholder = 'word or phrase to exclude';
      } else {
        this.builderValue.placeholder = 'word or phrase';
      }

      const hints = {
        match:            'Match: substring search (e.g. "wall" also matches "wallaby").',
        phrase:           'Exact match: whole-word/phrase only (e.g. "wall" will NOT match "wallaby").',
        exclude:          'Exclude: hides products that contain this word in the selected field.',
        'exclude-phrase': 'Exclude exact: hides only standalone occurrences (e.g. excludes "wall" but keeps "wallaby").',
        pin:              'Search the loaded collection by title or handle, then click to pin.',
        boost:            'Pin to top: every product matching this term floats to the top — nothing is hidden. Use the Field box to scope it (e.g. Title only).'
      };
      this.builderHint.textContent = hints[op] || '';
      this.hideTypeahead();
    }

    addRule() {
      const op = this.builderOp.value;        // match | phrase | exclude | exclude-phrase | pin | boost
      const raw = (this.builderValue.value || '').trim();
      if (!raw) {
        this.builderValue.focus();
        return;
      }

      let token;
      if (op === 'pin') {
        // Strip whitespace/quotes — handles never contain spaces. Lowercase to
        // match Shopify's convention.
        const handle = raw.replace(/\s+/g, '').replace(/"/g, '').toLowerCase();
        if (!handle) return;
        token = 'pin:' + handle;
      } else {
        const cleaned = raw.replace(/"/g, '');
        const field = this.builderField.value;
        const scope = field ? field + ':' : '';
        const negate = op === 'exclude' || op === 'exclude-phrase';
        const phrase = op === 'phrase' || op === 'exclude-phrase';
        // "boost" floats matching products to the top — prefix "^", never negated.
        const prefix = op === 'boost' ? '^' : (negate ? '-' : '');

        if (phrase) {
          // Quoted value → word-boundary match (whether negated or not).
          token = prefix + scope + '"' + cleaned + '"';
        } else {
          // Bare term(s). Multi-word input splits into per-word clauses with
          // the same prefix and scope so behavior stays predictable.
          const words = cleaned.split(/\s+/).filter(Boolean);
          token = words.map((w) => prefix + scope + w).join(' ');
        }
      }

      const current = (this.queryInput.value || '').replace(/\s+$/, '');
      this.queryInput.value = current ? current + ' ' + token : token;
      this.builderValue.value = '';
      this.previewLimit = PREVIEW_PAGE;
      this.render();
      this.builderValue.focus();
    }

    async loadCollection(handle) {
      if (!handle) {
        this.products = [];
        this.currentHandle = '';
          this.statusEl.textContent = 'Select a collection to begin.';
        this.resultsEl.innerHTML = '';
        return;
      }

      this.currentHandle = handle;
      this.products = [];
      this.isFetching = true;
      const token = ++this.fetchToken;
      this.statusEl.textContent = 'Loading products…';
      this.resultsEl.innerHTML = '';

      try {
        let page = 1;
        while (true) {
          const url = '/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + PRODUCTS_PER_PAGE + '&page=' + page;
          const response = await fetchWithRetry(url);
          if (token !== this.fetchToken) return; // stale

          const data = await response.json();
          const got = (data && data.products) || [];
          got.forEach((p) => window.CollectionSearchQuery.attachBodyText(p));
          this.products = this.products.concat(got);
    
          this.statusEl.textContent = 'Loaded ' + this.products.length + ' product' + (this.products.length === 1 ? '' : 's') + '…';
          this.render();

          if (got.length < PRODUCTS_PER_PAGE) break;
          page++;
          await sleep(PAGE_DELAY_MS);
        }
      } catch (err) {
        if (token !== this.fetchToken) return;
        const msg = err && err.message ? err.message : 'unknown';
        const friendly = /403/.test(msg)
          ? 'Shopify rate-limited the request after several retries. Wait a moment and pick the collection again.'
          : /404/.test(msg)
            ? 'Collection not found. Try a different one.'
            : 'Error loading: ' + msg;
        this.statusEl.textContent = friendly;
        return;
      } finally {
        if (token === this.fetchToken) this.isFetching = false;
      }

      this.render();
    }

    currentQueryString() {
      return (this.queryInput.value || '').trim();
    }

    render() {
      const lib = window.CollectionSearchQuery;
      if (!lib) return;

      const query = this.currentQueryString();
      const parsed = lib.parseQuery(query);

      const matched = this.products.filter((p) => lib.evaluateQuery(parsed, p));
      const ordered = lib.applyPins(parsed, matched, this.products);

      const pinSet = new Set(parsed.pins);
      const matchedSet = new Set(matched.map((p) => (p.handle || '').toLowerCase()));

      // Which matched products are floated up by a ^term boost (and aren't
      // already an explicit product pin).
      const boostSet = new Set();
      if (parsed.boosts && parsed.boosts.length) {
        matched.forEach((p) => {
          const handle = (p.handle || '').toLowerCase();
          if (pinSet.has(handle)) return;
          const fullText = lib.productFullText(p);
          if (parsed.boosts.some((b) => lib.matchesBoost(p, b, fullText))) {
            boostSet.add(handle);
          }
        });
      }

      this.parsedEl.textContent =
        parsed.clauses.length || parsed.pins.length || parsed.boosts.length
          ? '[' + parsed.clauses.length + ' clause' + (parsed.clauses.length === 1 ? '' : 's') +
            ', ' + parsed.pins.length + ' pin' + (parsed.pins.length === 1 ? '' : 's') +
            (parsed.boosts.length ? ', ' + parsed.boosts.length + ' boost' + (parsed.boosts.length === 1 ? '' : 's') : '') +
            ']'
          : '';

      const shown = Math.min(ordered.length, this.previewLimit);

      if (!this.currentHandle) {
        this.statusEl.textContent = 'Select a collection to begin.';
      } else if (this.isFetching) {
        this.statusEl.textContent = 'Loading… ' + matched.length + ' match' + (matched.length === 1 ? '' : 'es') + ' so far';
      } else {
        const matchedCount = matched.length;
        this.statusEl.textContent =
          matchedCount + ' match' + (matchedCount === 1 ? '' : 'es') +
          (parsed.pins.length ? ' • ' + parsed.pins.length + ' pinned' : '') +
          (boostSet.size ? ' • ' + boostSet.size + ' floated up' : '') +
          ' • showing ' + shown;
      }

      const slice = ordered.slice(0, shown);
      if (!slice.length) {
        this.resultsEl.innerHTML = this.currentHandle && !this.isFetching
          ? '<div class="qb-empty">No products match this query.</div>'
          : '';
        return;
      }

      const frag = document.createDocumentFragment();
      slice.forEach((product) => {
        frag.appendChild(this.buildCard(product, parsed, pinSet, matchedSet, boostSet));
      });

      if (ordered.length > shown) {
        const more = document.createElement('button');
        more.type = 'button';
        more.className = 'qb-load-more';
        more.setAttribute('data-qb-load-more', '');
        const remaining = ordered.length - shown;
        more.textContent = 'Show more (' + Math.min(remaining, PREVIEW_PAGE) + ' of ' + remaining + ' remaining)';
        frag.appendChild(more);
      }

      this.resultsEl.innerHTML = '';
      this.resultsEl.appendChild(frag);
    }

    buildCard(product, parsed, pinSet, matchedSet, boostSet) {
      const card = document.createElement('div');
      const handle = (product.handle || '').toLowerCase();
      const isPinned = pinSet.has(handle);
      const isBoosted = !isPinned && boostSet && boostSet.has(handle);
      card.className = 'qb-card' + (isPinned || isBoosted ? ' is-pinned' : '');

      const img = product.images && product.images[0]
        ? product.images[0].src.replace(/\.([^\.]+)$/, '_300x.$1')
        : 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';

      const pinLabel = isPinned ? 'Unpin' : 'Pin to top';
      const onlyByPin = isPinned && !matchedSet.has(handle);

      const badge = isPinned
        ? '<span class="qb-card-pin-badge">Pinned' + (onlyByPin ? ' (override)' : '') + '</span>'
        : (isBoosted ? '<span class="qb-card-pin-badge">Pinned term</span>' : '');

      card.innerHTML =
        badge +
        '<a href="/products/' + encodeURIComponent(product.handle) + '" target="_blank" rel="noopener">' +
          '<img src="' + escapeHtml(img) + '" alt="' + escapeHtml(product.title) + '" loading="lazy">' +
        '</a>' +
        '<div class="qb-card-body">' +
          '<h4 class="qb-card-title"><a href="/products/' + encodeURIComponent(product.handle) + '" target="_blank" rel="noopener">' + highlightTitle(product.title || '', parsed) + '</a></h4>' +
          '<div class="qb-card-handle">' + escapeHtml(product.handle || '') + '</div>' +
          '<button type="button" class="qb-card-pin-btn" data-handle="' + escapeHtml(product.handle || '') + '">' + pinLabel + '</button>' +
        '</div>';

      return card;
    }

    togglePin(handle) {
      if (!handle) return;
      const lib = window.CollectionSearchQuery;
      const parsed = lib.parseQuery(this.queryInput.value || '');
      const isPinned = parsed.pins.indexOf(handle.toLowerCase()) !== -1;

      let q = (this.queryInput.value || '').replace(/\s+$/, '');
      if (isPinned) {
        // Remove all `pin:handle` tokens for this handle (case-insensitive).
        const re = new RegExp('(^|\\s)-?pin:' + escapeRegex(handle) + '(?=\\s|$)', 'gi');
        q = q.replace(re, '').replace(/\s+/g, ' ').trim();
      } else {
        q = (q ? q + ' ' : '') + 'pin:' + handle;
      }
      this.queryInput.value = q;
      this.render();
    }

    async copyQuery() {
      const q = this.currentQueryString();
      const original = this.copyBtn.textContent;
      try {
        await navigator.clipboard.writeText(q);
        this.copyBtn.textContent = 'Copied!';
      } catch (e) {
        // Fallback for older browsers
        const ta = document.createElement('textarea');
        ta.value = q;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); this.copyBtn.textContent = 'Copied!'; }
        catch (_) { this.copyBtn.textContent = 'Copy failed'; }
        document.body.removeChild(ta);
      }
      setTimeout(() => { this.copyBtn.textContent = original; }, 1500);
    }
  }

  async function init() {
    const root = document.querySelector('.qb-page');
    if (!root || root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    await waitForQueryLib();
    new QueryBuilder(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(window, document);
